import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Handles the return leg of Emergent-managed Google sign-in.
 * Google drops the browser at {origin}/dashboard#session_id=... — we exchange that
 * one-time id on the backend, which sets the httpOnly session cookie.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = new URLSearchParams(location.hash.replace(/^#/, "")).get("session_id");
    const target = location.pathname && location.pathname !== "/" ? location.pathname : "/dashboard";

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        window.history.replaceState(null, "", target);
        toast.success(`Namaste, ${data.name?.split(" ")[0] || "friend"}`);
        nav(target, { replace: true, state: { user: data } });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
        window.history.replaceState(null, "", "/login");
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, location.pathname, nav, setUser]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="auth-callback">
      <div className="font-display text-2xl text-muted-foreground">Signing you in…</div>
    </div>
  );
}
